import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CreditCard, Banknote, CheckCircle, AlertCircle, ChevronRight, Lock } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, customerPaymentsApi, paymentService } from '../services/api';
import type { CustomerJobPaymentSummaryItem, CustomerPaymentSummaryDto } from '../services/api';
import { JobStatus } from '../types';
import type { MonthlyStatementDto } from '../types';

const PAY_STATUS_LABELS: Record<number, string> = {
  1: 'Pending', 2: 'Processing', 3: 'Released', 4: 'Failed', 5: 'Refunded', 6: 'On Hold',
};
const PAY_STATUS_STYLE: Record<number, React.CSSProperties> = {
  1: { background: '#fef3c7', color: '#92400e' },
  2: { background: '#dbeafe', color: '#1d4ed8' },
  3: { background: '#dcfce7', color: '#166534' },
  4: { background: '#fee2e2', color: '#991b1b' },
  5: { background: '#f3e8ff', color: '#7c3aed' },
  6: { background: '#f3f4f6', color: '#374151' },
};

const JOB_STATUS_LABELS: Record<number, string> = {
  4: 'Accepted', 5: 'Contract Sent', 6: 'In Progress',
  7: 'Completed', 8: 'POD Verified', 9: 'Paid', 10: 'Reviewed', 13: 'Carrier Accepted',
};

const MIN_ONLINE_PCT = 0.25;

const needsPayment = (item: CustomerJobPaymentSummaryItem) =>
  (item.jobStatus === JobStatus.CarrierAccepted ||
   item.jobStatus === 4 ||
   (item.jobStatus >= 6 && item.jobStatus <= 8)) &&
  !item.paymentStatus;

const isPending = (item: CustomerJobPaymentSummaryItem) =>
  item.paymentStatus === 1 || item.paymentStatus === 2;

const isPaid = (item: CustomerJobPaymentSummaryItem) =>
  item.paymentStatus === 3;

interface SplitState {
  onlineAmount: string;
  codAmount: string;
  error: string | null;
}

type PageTab = 'payments' | 'statement';

export default function CustomerPaymentsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getToken, account } = useAppAuth();

  const [summary, setSummary]     = useState<CustomerPaymentSummaryDto | null>(null);
  const [loading, setLoading]     = useState(true);
  const [splits, setSplits]       = useState<Record<string, SplitState>>({});
  const [paying, setPaying]       = useState(false);
  const [apiError, setApiError]   = useState<string | null>(null);
  const [pageTab, setPageTab]     = useState<PageTab>('payments');
  const [isMobile, setIsMobile]   = useState(() => window.innerWidth < 640);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Monthly statement state
  const now = new Date();
  const [stmtYear, setStmtYear]   = useState(now.getFullYear());
  const [stmtMonth, setStmtMonth] = useState(now.getMonth() + 1);
  const [stmt, setStmt]           = useState<MonthlyStatementDto | null>(null);
  const [stmtLoading, setStmtLoading] = useState(false);
  const [stmtError, setStmtError] = useState<string | null>(null);

  const [paymentResult, setPaymentResult]     = useState<'success' | 'cancelled' | null>(null);
  const [confirmedJobIds, setConfirmedJobIds] = useState<string[]>([]);
  const [expandedSummary, setExpandedSummary] = useState<'total' | 'online' | 'cod' | null>(null);
  const confirmFiredRef = useRef(false);

  const load = useCallback(async () => {
    if (!account) { setLoading(false); return; }
    setLoading(true);
    try {
      const token = await getToken();
      const data  = await customerPaymentsApi.getSummary(token);
      setSummary(data);

      const initSplits: Record<string, SplitState> = {};
      data.jobs.forEach(item => {
        if (needsPayment(item)) {
          initSplits[item.jobId] = {
            onlineAmount: item.onlineAmount?.toFixed(2) ?? item.totalAmount.toFixed(2),
            codAmount:    item.codAmount?.toFixed(2)    ?? '0.00',
            error: null,
          };
        }
      });
      setSplits(initSplits);
    } catch {
      setApiError('Failed to load payment summary. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [account, getToken]);

  const loadStatement = useCallback(async (y: number, m: number) => {
    if (!account) return;
    setStmtLoading(true);
    setStmtError(null);
    try {
      const token = await getToken();
      const client = createApiClient(() => Promise.resolve(token));
      const data = await paymentService(client).getMonthlyStatement(y, m);
      setStmt(data);
    } catch {
      setStmtError('Failed to load statement. Please try again.');
    } finally {
      setStmtLoading(false);
    }
  }, [account, getToken]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (pageTab === 'statement') loadStatement(stmtYear, stmtMonth);
  }, [pageTab, stmtYear, stmtMonth, loadStatement]);

  useEffect(() => {
    // account is populated asynchronously by MSAL; wait until it is ready
    // before calling getToken(), otherwise the silent token acquisition throws
    // and the non-fatal catch swallows the error, leaving the payment unconfirmed.
    if (!account || confirmFiredRef.current) return;

    const result  = searchParams.get('payment');
    const isBatch = searchParams.get('batch') === 'true';
    const jobId   = searchParams.get('jobId');
    const jobIds  = searchParams.get('jobIds');

    if (result === 'success' || result === 'cancelled') {
      setPaymentResult(result as 'success' | 'cancelled');
      if (result === 'success') {
        confirmFiredRef.current = true;
        if (isBatch && jobIds) {
          const ids = decodeURIComponent(jobIds).split(',').filter(Boolean);
          setConfirmedJobIds(ids);
          batchConfirmPayments(ids);
        } else if (jobId) {
          setConfirmedJobIds([jobId]);
          confirmSinglePayment(jobId);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const confirmSinglePayment = async (jobId: string) => {
    try {
      const token = await getToken();
      await customerPaymentsApi.confirmPayment(jobId, token);
      await load();
    } catch { /* non-fatal */ }
  };

  const batchConfirmPayments = async (jobIds: string[]) => {
    try {
      const token = await getToken();
      await customerPaymentsApi.batchConfirmPayment(jobIds, token);
      await load();
    } catch { /* non-fatal */ }
  };

  const highlightJobId = searchParams.get('jobId') ?? (confirmedJobIds.length === 1 ? confirmedJobIds[0] : null);

  const updateSplit = (jobId: string, field: 'onlineAmount' | 'codAmount', value: string) => {
    setSplits(prev => {
      const item    = summary?.jobs.find(j => j.jobId === jobId);
      const total   = item?.totalAmount ?? 0;
      const current = { ...prev[jobId], [field]: value, error: null as string | null };

      const online = parseFloat(field === 'onlineAmount' ? value : current.onlineAmount) || 0;
      const cod    = parseFloat(field === 'codAmount'    ? value : current.codAmount)    || 0;

      if (field === 'onlineAmount') {
        current.codAmount = Math.max(0, total - online).toFixed(2);
      } else {
        current.onlineAmount = Math.max(0, total - cod).toFixed(2);
      }

      const finalOnline = parseFloat(current.onlineAmount) || 0;
      if (finalOnline < total * MIN_ONLINE_PCT - 0.01) {
        current.error = `Min 25% = £${(total * MIN_ONLINE_PCT).toFixed(2)}`;
      }

      return { ...prev, [jobId]: current };
    });
  };

  const unpaidJobs = summary?.jobs.filter(needsPayment) ?? [];

  const validateAll = (): boolean => {
    let valid = true;
    setSplits(prev => {
      const next = { ...prev };
      unpaidJobs.forEach(item => {
        const s = next[item.jobId];
        if (!s) return;
        const online = parseFloat(s.onlineAmount) || 0;
        const cod    = parseFloat(s.codAmount)    || 0;
        const min    = item.totalAmount * MIN_ONLINE_PCT;
        if (online < min - 0.01) {
          next[item.jobId] = { ...s, error: `Min 25% = £${min.toFixed(2)}` };
          valid = false;
        } else if (Math.abs(online + cod - item.totalAmount) > 0.01) {
          next[item.jobId] = { ...s, error: 'Online + COD must equal total.' };
          valid = false;
        }
      });
      return next;
    });
    return valid;
  };

  const handlePayAll = async () => {
    if (!validateAll()) return;

    const payload = unpaidJobs.map(item => ({
      jobId:        item.jobId,
      onlineAmount: parseFloat(splits[item.jobId]?.onlineAmount ?? item.totalAmount.toFixed(2)) || item.totalAmount,
      codAmount:    parseFloat(splits[item.jobId]?.codAmount    ?? '0') || 0,
    }));

    if (payload.length === 0) return;

    setPaying(true);
    setApiError(null);
    try {
      const token  = await getToken();
      const result = await customerPaymentsApi.createBatchSplitCheckout(payload, token);
      window.location.href = result.checkoutUrl;
    } catch (err: any) {
      setApiError(err.message ?? 'Failed to create payment link. Please try again.');
      setPaying(false);
    }
  };

  const totalOnlineSelected = unpaidJobs.reduce((sum, item) =>
    sum + (parseFloat(splits[item.jobId]?.onlineAmount ?? '0') || 0), 0);
  const totalCodSelected = unpaidJobs.reduce((sum, item) =>
    sum + (parseFloat(splits[item.jobId]?.codAmount ?? '0') || 0), 0);
  const hasErrors = unpaidJobs.some(item => !!splits[item.jobId]?.error);

  if (loading) return (
    <div style={s.center}><div style={s.spinner} /></div>
  );

  const unpaid  = summary?.jobs.filter(needsPayment) ?? [];
  const pending = summary?.jobs.filter(isPending)     ?? [];
  const paid    = summary?.jobs.filter(isPaid)        ?? [];
  const other   = summary?.jobs.filter(j => !needsPayment(j) && !isPending(j) && !isPaid(j)) ?? [];

  const sections: Array<{ title: string; jobs: CustomerJobPaymentSummaryItem[] }> = [];
  if (unpaid.length)   sections.push({ title: `Ready to Pay (${unpaid.length})`,      jobs: unpaid });
  if (pending.length)  sections.push({ title: `Processing (${pending.length})`,        jobs: pending });
  if (other.length)    sections.push({ title: `Active Jobs (${other.length})`,         jobs: other });
  if (paid.length)     sections.push({ title: `Payment History (${paid.length})`,      jobs: paid });

  return (
    <div style={s.page}>
      {paymentResult === 'success' && (
        <div style={s.successBanner}>
          <CheckCircle size={18} color="#166534" />
          <span>
            Payment confirmed! Your driver{confirmedJobIds.length > 1 ? 's have' : ' has'} been notified and a contract has been drafted.
          </span>
          <button style={s.bannerClose} onClick={() => setPaymentResult(null)}>✕</button>
        </div>
      )}
      {paymentResult === 'cancelled' && (
        <div style={s.cancelBanner}>
          <AlertCircle size={18} color="#92400e" />
          <span>Payment was cancelled. You can retry below.</span>
          <button style={s.bannerClose} onClick={() => setPaymentResult(null)}>✕</button>
        </div>
      )}

      <h1 style={s.heading}>My Payments</h1>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 20 }}>
        {([['payments', '💳 Payments'], ['statement', '📋 Monthly Statement']] as [PageTab, string][]).map(([key, label]) => (
          <button key={key} onClick={() => setPageTab(key)} style={{
            padding: '9px 18px', border: 'none', background: 'transparent', fontFamily: 'inherit',
            color: pageTab === key ? '#1d4ed8' : '#6b7280',
            fontWeight: pageTab === key ? 700 : 400,
            borderBottom: pageTab === key ? '2px solid #1d4ed8' : '2px solid transparent',
            fontSize: 13, cursor: 'pointer',
          }}>{label}</button>
        ))}
      </div>

      {/* ── Monthly Statement tab ── */}
      {pageTab === 'statement' && (
        <div>
          {/* Month picker */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <button
              onClick={() => { if (stmtMonth === 1) { setStmtYear(y => y - 1); setStmtMonth(12); } else setStmtMonth(m => m - 1); }}
              style={s.navBtn}
            >‹</button>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#111827', minWidth: 140, textAlign: 'center' as const }}>
              {stmt?.monthName ?? new Date(stmtYear, stmtMonth - 1).toLocaleString('en-GB', { month: 'long' })} {stmtYear}
            </span>
            <button
              onClick={() => { if (stmtMonth === 12) { setStmtYear(y => y + 1); setStmtMonth(1); } else setStmtMonth(m => m + 1); }}
              disabled={stmtYear === now.getFullYear() && stmtMonth === now.getMonth() + 1}
              style={{ ...s.navBtn, opacity: (stmtYear === now.getFullYear() && stmtMonth === now.getMonth() + 1) ? 0.4 : 1 }}
            >›</button>
            <button onClick={() => loadStatement(stmtYear, stmtMonth)} style={s.navBtn}>↻</button>
          </div>

          {stmtError && (
            <div style={{ background: '#fee2e2', borderRadius: 8, padding: '10px 14px', color: '#991b1b', fontSize: 13, marginBottom: 16 }}>{stmtError}</div>
          )}
          {stmtLoading && <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>Loading statement…</div>}

          {!stmtLoading && stmt && (
            <>
              {/* Summary row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
                {[
                  { label: 'Total Spent',   value: `£${stmt.totalGross.toFixed(2)}`,   color: '#1d4ed8', bg: '#eff6ff' },
                  { label: 'Paid Online',   value: `£${stmt.totalOnline.toFixed(2)}`,  color: '#1d4ed8', bg: '#eff6ff' },
                  { label: 'COD',           value: `£${stmt.totalCod.toFixed(2)}`,     color: '#92400e', bg: '#fef3c7' },
                  { label: 'Jobs',          value: String(stmt.lines.length),           color: '#374151', bg: '#f9fafb' },
                ].map(c => (
                  <div key={c.label} style={{ background: c.bg, borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.4 }}>{c.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: c.color }}>{c.value}</div>
                  </div>
                ))}
              </div>

              {stmt.lines.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
                  No payment activity for {stmt.monthName} {stmt.year}.
                </div>
              ) : (
                <div style={s.tableWrap}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={{ ...s.th, textAlign: 'left' as const }}>Date</th>
                        <th style={{ ...s.th, textAlign: 'left' as const }}>Job</th>
                        <th style={s.th}>Total</th>
                        <th style={s.th}>Online</th>
                        <th style={s.th}>COD</th>
                        <th style={s.th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stmt.lines.map((line, i) => (
                        <tr key={line.paymentId || i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={s.td}>
                            <span style={{ fontSize: 12, color: '#6b7280' }}>
                              {new Date(line.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                            </span>
                          </td>
                          <td style={s.td}>
                            <div style={s.jobRef}>#{line.jobReference || line.jobId.slice(0, 8).toUpperCase()}</div>
                          </td>
                          <td style={{ ...s.td, ...s.tdNum }}>
                            <span style={s.totalAmt}>£{line.grossAmount.toFixed(2)}</span>
                          </td>
                          <td style={{ ...s.td, ...s.tdNum }}>
                            <span style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 600 }}>
                              {line.onlineAmount > 0 ? `£${line.onlineAmount.toFixed(2)}` : '—'}
                            </span>
                          </td>
                          <td style={{ ...s.td, ...s.tdNum }}>
                            <span style={{ fontSize: 13, color: '#92400e', fontWeight: 600 }}>
                              {line.codAmount > 0 ? `£${line.codAmount.toFixed(2)}` : '—'}
                            </span>
                          </td>
                          <td style={{ ...s.td, ...s.tdNum }}>
                            <span style={{ ...s.badge, ...stmtStatusStyle(line.status) }}>{line.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ padding: '10px 14px', background: '#f9fafb', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => exportCsv(stmt)}
                      style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, color: '#1d4ed8', cursor: 'pointer' }}
                    >
                      ⬇ Export CSV
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Payments tab ── */}
      {pageTab === 'payments' && (
      <>

      {summary && (
        <div style={s.cards}>
          {/* Total jobs value — click to expand per-job breakdown */}
          <div style={{ ...s.card, background: '#f0fdf4', cursor: 'pointer' }}
            onClick={() => setExpandedSummary(expandedSummary === 'total' ? null : 'total')}>
            <span style={s.cardIcon}>💰</span>
            <span style={s.cardLabel}>Total jobs value</span>
            <span style={{ ...s.cardValue, color: '#166534' }}>£{summary.totalJobsValue.toFixed(2)}</span>
            <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 700 }}>{expandedSummary === 'total' ? '▲ Hide' : '▼ Details'}</span>
          </div>
          {/* Paid online — click to see which jobs contributed */}
          <div style={{ ...s.card, background: '#eff6ff', cursor: 'pointer' }}
            onClick={() => setExpandedSummary(expandedSummary === 'online' ? null : 'online')}>
            <span style={s.cardIcon}><CreditCard size={22} color="#1d4ed8" /></span>
            <span style={s.cardLabel}>Paid online</span>
            <span style={{ ...s.cardValue, color: '#1d4ed8' }}>£{summary.totalOnlinePaid.toFixed(2)}</span>
            <span style={{ fontSize: 10, color: '#1d4ed8', fontWeight: 700 }}>{expandedSummary === 'online' ? '▲ Hide' : '▼ Details'}</span>
          </div>
          {/* COD outstanding — click to see which jobs have COD */}
          <div style={{ ...s.card, background: '#fefce8', cursor: 'pointer' }}
            onClick={() => setExpandedSummary(expandedSummary === 'cod' ? null : 'cod')}>
            <span style={s.cardIcon}><Banknote size={22} color="#92400e" /></span>
            <span style={s.cardLabel}>COD outstanding</span>
            <span style={{ ...s.cardValue, color: '#92400e' }}>£{summary.totalCodOwed.toFixed(2)}</span>
            <span style={{ fontSize: 10, color: '#92400e', fontWeight: 700 }}>{expandedSummary === 'cod' ? '▲ Hide' : '▼ Details'}</span>
          </div>
        </div>
      )}

      {/* Expandable summary detail panels */}
      {summary && expandedSummary === 'total' && (
        <div style={s.expandPanel}>
          <p style={{ fontWeight: 700, color: '#166534', fontSize: 13, marginBottom: 8 }}>All Jobs — Value Breakdown</p>
          {summary.jobs.map(item => (
            <div key={item.jobId} style={s.expandRow}>
              <span style={{ color: '#374151' }}>{item.jobTitle}</span>
              <span style={{ color: '#9ca3af', fontSize: 11 }}>#{item.jobReference}</span>
              <span style={{ fontWeight: 700, color: '#166534', marginLeft: 'auto' }}>£{item.totalAmount.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
      {summary && expandedSummary === 'online' && (
        <div style={s.expandPanel}>
          <p style={{ fontWeight: 700, color: '#1d4ed8', fontSize: 13, marginBottom: 8 }}>Online Payments Received</p>
          {summary.jobs.filter(j => (j.onlineAmount ?? 0) > 0).length === 0
            ? <p style={{ color: '#9ca3af', fontSize: 12 }}>No online payments recorded yet.</p>
            : summary.jobs.filter(j => (j.onlineAmount ?? 0) > 0).map(item => (
              <div key={item.jobId} style={s.expandRow}>
                <span style={{ color: '#374151' }}>{item.jobTitle}</span>
                <span style={{ color: '#9ca3af', fontSize: 11 }}>#{item.jobReference}</span>
                <span style={{ fontWeight: 700, color: '#1d4ed8', marginLeft: 'auto' }}>£{(item.onlineAmount ?? 0).toFixed(2)}</span>
              </div>
            ))}
        </div>
      )}
      {summary && expandedSummary === 'cod' && (
        <div style={s.expandPanel}>
          <p style={{ fontWeight: 700, color: '#92400e', fontSize: 13, marginBottom: 8 }}>COD Outstanding by Job</p>
          {summary.jobs.filter(j => (j.codAmount ?? 0) > 0).length === 0
            ? <p style={{ color: '#9ca3af', fontSize: 12 }}>No COD amounts outstanding.</p>
            : summary.jobs.filter(j => (j.codAmount ?? 0) > 0).map(item => (
              <div key={item.jobId} style={s.expandRow}>
                <span style={{ color: '#374151' }}>{item.jobTitle}</span>
                <span style={{ color: '#9ca3af', fontSize: 11 }}>#{item.jobReference}</span>
                <span style={{ fontWeight: 700, color: '#92400e', marginLeft: 'auto' }}>£{(item.codAmount ?? 0).toFixed(2)}</span>
              </div>
            ))}
          <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>COD is collected by the driver at delivery.</p>
        </div>
      )}

      {apiError && (
        <div style={s.errorBox}>
          <AlertCircle size={16} color="#991b1b" />
          <span>{apiError}</span>
        </div>
      )}

      {sections.length > 0 && (isMobile ? (
        <div>
          {sections.map(({ title, jobs }) => (
            <div key={title} style={{ marginBottom: 16 }}>
              <div style={s.sectionRow}>{title}</div>
              {jobs.map(item => {
                const locked         = !needsPayment(item);
                const split          = splits[item.jobId];
                const total          = item.totalAmount;
                const minPct         = Math.ceil(total * MIN_ONLINE_PCT * 100) / 100;
                const highlight      = item.jobId === highlightJobId;
                const payStatus      = item.paymentStatus;
                const jobStatusLabel = JOB_STATUS_LABELS[item.jobStatus] ?? 'Active';

                return (
                  <div key={item.jobId} style={{
                    background: highlight ? '#eff6ff' : locked ? '#fafafa' : '#fff',
                    border: `1px solid ${highlight ? '#bfdbfe' : '#e5e7eb'}`,
                    borderRadius: 12, padding: '14px 14px', marginBottom: 10,
                  }}>
                    {/* Header row: ref + total + status */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                      <div style={{ flex: 1, minWidth: 0, marginRight: 10 }}>
                        <div style={s.jobRef}>#{item.jobReference}</div>
                        <div style={{ ...s.jobTitle, whiteSpace: 'normal', wordBreak: 'break-word' }}>{item.jobTitle}</div>
                        <div style={{ ...s.carrierName, marginTop: 2 }}>{item.carrierName}</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                        <span style={s.totalAmt}>£{total.toFixed(2)}</span>
                        {payStatus ? (
                          <span style={{ ...s.badge, ...PAY_STATUS_STYLE[payStatus] }}>{PAY_STATUS_LABELS[payStatus]}</span>
                        ) : (
                          <span style={s.jobBadge}>{jobStatusLabel}</span>
                        )}
                      </div>
                    </div>

                    {/* Payment inputs */}
                    {!locked ? (
                      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>
                            <CreditCard size={10} style={{ marginRight: 3, verticalAlign: 'middle' }} />Pay online (min 25%)
                          </div>
                          <div style={{ ...s.inputWrap, ...(split?.error ? s.inputWrapError : {}), width: '100%' }}>
                            <span style={s.currency}>£</span>
                            <input
                              type="number" min={minPct} max={total} step="0.01"
                              style={{ ...s.input, flex: 1 }}
                              value={split?.onlineAmount ?? total.toFixed(2)}
                              onChange={e => updateSplit(item.jobId, 'onlineAmount', e.target.value)}
                            />
                          </div>
                          <span style={s.hint}>min £{minPct.toFixed(2)}</span>
                          {split?.error && <div style={{ ...s.cellError, textAlign: 'left', maxWidth: 'none' }}>{split.error}</div>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>
                            <Banknote size={10} style={{ marginRight: 3, verticalAlign: 'middle' }} />COD (driver collects)
                          </div>
                          <div style={{ ...s.inputWrap, width: '100%' }}>
                            <span style={s.currency}>£</span>
                            <input
                              type="number" min={0} max={total * (1 - MIN_ONLINE_PCT)} step="0.01"
                              style={{ ...s.input, flex: 1 }}
                              value={split?.codAmount ?? '0.00'}
                              onChange={e => updateSplit(item.jobId, 'codAmount', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                        {(item.onlineAmount ?? 0) > 0 && (
                          <div>
                            <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>Paid online</div>
                            <span style={s.lockedAmt}><Lock size={10} style={{ marginRight: 3, opacity: 0.4 }} />£{(item.onlineAmount ?? total).toFixed(2)}</span>
                          </div>
                        )}
                        {(item.codAmount ?? 0) > 0 && (
                          <div>
                            <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>COD</div>
                            <span style={s.lockedAmt}><Lock size={10} style={{ marginRight: 3, opacity: 0.4 }} />£{(item.codAmount ?? 0).toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, textAlign: 'left' }}>Job</th>
                <th style={{ ...s.th, textAlign: 'left' }}>Carrier</th>
                <th style={s.th}>Total</th>
                <th style={s.th}>
                  <span style={s.thIcon}><CreditCard size={11} /></span>Pay Online
                </th>
                <th style={s.th}>
                  <span style={s.thIcon}><Banknote size={11} /></span>COD
                </th>
                <th style={s.th}>Status</th>
              </tr>
            </thead>
            {sections.map(({ title, jobs }) => (
              <tbody key={title}>
                <tr>
                  <td colSpan={6} style={s.sectionRow}>{title}</td>
                </tr>
                {jobs.map(item => {
                  const locked    = !needsPayment(item);
                  const split     = splits[item.jobId];
                  const total     = item.totalAmount;
                  const minPct    = Math.ceil(total * MIN_ONLINE_PCT * 100) / 100;
                  const highlight = item.jobId === highlightJobId;
                  const payStatus = item.paymentStatus;
                  const jobStatusLabel = JOB_STATUS_LABELS[item.jobStatus] ?? 'Active';

                  return (
                    <tr
                      key={item.jobId}
                      style={{
                        ...s.row,
                        ...(locked ? s.rowLocked : s.rowUnpaid),
                        ...(highlight ? s.rowHighlight : {}),
                      }}
                    >
                      <td style={s.td}>
                        <div style={s.jobRef}>#{item.jobReference}</div>
                        <div style={s.jobTitle}>{item.jobTitle}</div>
                      </td>
                      <td style={s.td}>
                        <span style={s.carrierName}>{item.carrierName}</span>
                      </td>
                      <td style={{ ...s.td, ...s.tdNum }}>
                        <span style={s.totalAmt}>£{total.toFixed(2)}</span>
                      </td>
                      <td style={{ ...s.td, ...s.tdNum }}>
                        {locked ? (
                          <span style={s.lockedAmt}>
                            <Lock size={10} style={{ marginRight: 3, opacity: 0.4 }} />
                            £{(item.onlineAmount ?? total).toFixed(2)}
                          </span>
                        ) : (
                          <div style={s.inputCell}>
                            <div style={{ ...s.inputWrap, ...(split?.error ? s.inputWrapError : {}) }}>
                              <span style={s.currency}>£</span>
                              <input
                                type="number" min={minPct} max={total} step="0.01"
                                style={s.input}
                                value={split?.onlineAmount ?? total.toFixed(2)}
                                onChange={e => updateSplit(item.jobId, 'onlineAmount', e.target.value)}
                              />
                            </div>
                            <span style={s.hint}>min £{minPct.toFixed(2)}</span>
                            {split?.error && <span style={s.cellError}>{split.error}</span>}
                          </div>
                        )}
                      </td>
                      <td style={{ ...s.td, ...s.tdNum }}>
                        {locked ? (
                          <span style={s.lockedAmt}>
                            <Lock size={10} style={{ marginRight: 3, opacity: 0.4 }} />
                            £{(item.codAmount ?? 0).toFixed(2)}
                          </span>
                        ) : (
                          <div style={s.inputCell}>
                            <div style={s.inputWrap}>
                              <span style={s.currency}>£</span>
                              <input
                                type="number" min={0} max={total * (1 - MIN_ONLINE_PCT)} step="0.01"
                                style={s.input}
                                value={split?.codAmount ?? '0.00'}
                                onChange={e => updateSplit(item.jobId, 'codAmount', e.target.value)}
                              />
                            </div>
                            <span style={s.hint}>driver collects</span>
                          </div>
                        )}
                      </td>
                      <td style={{ ...s.td, ...s.tdNum }}>
                        {payStatus ? (
                          <span style={{ ...s.badge, ...PAY_STATUS_STYLE[payStatus] }}>
                            {PAY_STATUS_LABELS[payStatus]}
                          </span>
                        ) : (
                          <span style={s.jobBadge}>{jobStatusLabel}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </table>
        </div>
      ))}

      {unpaid.length > 0 && (
        <div style={s.footer}>
          <div style={s.footerRow}>
            <span style={s.footerLabel}>Total to pay online now:</span>
            <span style={{ ...s.footerValue, color: '#1d4ed8' }}>£{totalOnlineSelected.toFixed(2)}</span>
          </div>
          <div style={s.footerRow}>
            <span style={s.footerLabel}>Total COD (driver collects at delivery):</span>
            <span style={{ ...s.footerValue, color: '#92400e' }}>£{totalCodSelected.toFixed(2)}</span>
          </div>
          <div style={{ ...s.footerRow, borderTop: '1px solid #e5e7eb', paddingTop: 10, marginTop: 4 }}>
            <span style={{ ...s.footerLabel, fontWeight: 700 }}>Total outstanding:</span>
            <span style={{ ...s.footerValue, color: '#111827', fontWeight: 800, fontSize: 18 }}>
              £{(totalOnlineSelected + totalCodSelected).toFixed(2)}
            </span>
          </div>
          <button
            style={{ ...s.payBtn, opacity: (paying || hasErrors) ? 0.6 : 1, marginTop: 12 }}
            disabled={paying || hasErrors}
            onClick={handlePayAll}
          >
            {paying ? 'Redirecting to Stripe…' : (
              <>
                <CreditCard size={15} style={{ marginRight: 6 }} />
                Pay £{totalOnlineSelected.toFixed(2)} online via Stripe
                {unpaid.length > 1 ? ` (${unpaid.length} jobs)` : ''}
                <ChevronRight size={15} style={{ marginLeft: 4 }} />
              </>
            )}
          </button>
        </div>
      )}

      {!loading && summary?.jobs.length === 0 && (
        <div style={s.empty}>
          <CreditCard size={40} color="#9ca3af" />
          <p style={{ color: '#6b7280', marginTop: 12 }}>No jobs with accepted quotes yet.</p>
          <button style={s.emptyBtn} onClick={() => navigate('/post-job')}>Post a Job</button>
        </div>
      )}

      </>
      )}
    </div>
  );
}

function stmtStatusStyle(status: string): React.CSSProperties {
  const map: Record<string, React.CSSProperties> = {
    Released:   { background: '#dcfce7', color: '#166534' },
    Processing: { background: '#dbeafe', color: '#1d4ed8' },
    Pending:    { background: '#fef3c7', color: '#92400e' },
    Failed:     { background: '#fee2e2', color: '#991b1b' },
    Refunded:   { background: '#f3e8ff', color: '#7c3aed' },
    Unpaid:     { background: '#f3f4f6', color: '#374151' },
  };
  return map[status] ?? { background: '#f3f4f6', color: '#374151' };
}

function exportCsv(stmt: MonthlyStatementDto) {
  const rows = [
    ['Date', 'Job Ref', 'Total', 'Online', 'COD', 'Status'],
    ...stmt.lines.map(l => [
      new Date(l.date).toLocaleDateString('en-GB'),
      l.jobReference || l.jobId.slice(0, 8),
      l.grossAmount.toFixed(2),
      l.onlineAmount.toFixed(2),
      l.codAmount.toFixed(2),
      l.status,
    ]),
    ['', 'TOTAL', stmt.totalGross.toFixed(2), stmt.totalOnline.toFixed(2), stmt.totalCod.toFixed(2), ''],
  ];
  const csv = rows.map(r => r.join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = `customer-statement-${stmt.year}-${String(stmt.month).padStart(2, '0')}.csv`;
  a.click();
}

const s: Record<string, React.CSSProperties> = {
  page:         { width: '100%', padding: '24px 16px 100px' },
  center:       { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 },
  spinner:      { width: 36, height: 36, border: '4px solid #dbeafe', borderTop: '4px solid #1d4ed8', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  heading:      { fontSize: 22, fontWeight: 800, color: '#111827', marginBottom: 20 },
  cards:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24 },
  card:         { borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4, border: '1px solid #e5e7eb' },
  cardIcon:     { fontSize: 22 },
  cardLabel:    { fontSize: 11, color: '#6b7280', fontWeight: 600 },
  cardValue:    { fontSize: 20, fontWeight: 800 },

  tableWrap:    { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden', marginBottom: 16 },
  table:        { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:           { padding: '10px 14px', textAlign: 'right', color: '#6b7280', fontWeight: 600, fontSize: 11, borderBottom: '1px solid #e5e7eb', background: '#f9fafb', whiteSpace: 'nowrap' },
  thIcon:       { display: 'inline-flex', alignItems: 'center', marginRight: 4, verticalAlign: 'middle' },
  sectionRow:   { padding: '8px 14px', fontSize: 12, fontWeight: 700, color: '#374151', background: '#f3f4f6', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' },
  row:          { borderBottom: '1px solid #f3f4f6', transition: 'background 0.15s' },
  rowUnpaid:    { background: '#fff' },
  rowLocked:    { background: '#fafafa' },
  rowHighlight: { background: '#eff6ff' },
  td:           { padding: '12px 14px', verticalAlign: 'top' },
  tdNum:        { textAlign: 'right' },

  jobRef:       { fontSize: 10, color: '#9ca3af', fontWeight: 600, marginBottom: 2 },
  jobTitle:     { fontSize: 13, fontWeight: 700, color: '#111827' },
  carrierName:  { fontSize: 12, color: '#6b7280' },
  totalAmt:     { fontSize: 15, fontWeight: 800, color: '#1d4ed8' },
  lockedAmt:    { display: 'inline-flex', alignItems: 'center', fontSize: 13, fontWeight: 600, color: '#6b7280' },

  inputCell:    { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 },
  inputWrap:    { display: 'flex', alignItems: 'center', border: '1.5px solid #d1d5db', borderRadius: 7, overflow: 'hidden', background: '#f9fafb', width: 110 },
  inputWrapError: { borderColor: '#f87171', background: '#fff5f5' },
  currency:     { padding: '0 6px', fontSize: 12, color: '#6b7280', fontWeight: 600 },
  input:        { width: '100%', border: 'none', background: 'transparent', padding: '6px 6px 6px 0', fontSize: 13, fontWeight: 600, color: '#111827', outline: 'none', textAlign: 'right' },
  hint:         { fontSize: 10, color: '#9ca3af' },
  cellError:    { fontSize: 10, color: '#dc2626', fontWeight: 600, maxWidth: 120, textAlign: 'right' },

  badge:        { fontSize: 11, borderRadius: 6, padding: '2px 8px', display: 'inline-block', fontWeight: 600 },
  jobBadge:     { fontSize: 11, borderRadius: 6, padding: '2px 8px', display: 'inline-block', background: '#f3f4f6', color: '#374151', fontWeight: 600 },

  footer:       { position: 'sticky', bottom: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: '16px 20px', boxShadow: '0 -4px 16px rgba(0,0,0,0.07)', marginTop: 8 },
  footerRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  footerLabel:  { fontSize: 13, color: '#6b7280' },
  footerValue:  { fontSize: 16, fontWeight: 700 },
  payBtn:       { width: '100%', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },

  expandPanel:  { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13 },
  expandRow:    { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap' as const },
  errorBox:     { display: 'flex', alignItems: 'center', gap: 8, background: '#fee2e2', borderRadius: 10, padding: '10px 14px', marginBottom: 18, fontSize: 13, color: '#991b1b' },
  successBanner:{ display: 'flex', alignItems: 'center', gap: 10, background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 14, color: '#166534' },
  cancelBanner: { display: 'flex', alignItems: 'center', gap: 10, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 14, color: '#92400e' },
  bannerClose:  { marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, lineHeight: 1 },
  empty:        { textAlign: 'center', padding: '60px 20px' },
  emptyBtn:     { marginTop: 16, background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  navBtn:       { padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 16, fontFamily: 'inherit', fontWeight: 700 as const },
};
